import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  Card,
  MultipleSelector,
  type Option,
  PrimaryFullScreenSpinner,
  Txt,
  toast,
  useAppForm,
} from "@repo/ui";
import {
  dateSchema,
  formatRate,
  getCountryLists,
  NotFoundError,
  neverFunc,
} from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import type Decimal from "decimal.js";
import { ExternalLink } from "lucide-react";
import { DateTime } from "luxon";
import { Suspense, useRef, useState } from "react";
import { PiPencilSimple } from "react-icons/pi";
import { z } from "zod";
import { EditRatesModal } from "~/components/Product/EditRatesModal";
import { RateScrapingModal } from "~/components/Product/RateScrapingModal";
import { RateSourceBadge } from "~/components/Product/RateSourceBadge";
import {
  type BankAccountRateFlattened,
  flattenBankAccountRate,
} from "~/components/Product/RateUpdates";
import { graphql } from "~/gql/client";
import type { PossibleTypesResultData } from "~/gql/client/possibleTypes";
import { opsHead } from "~/head";

const formSchema = z.object({
  name: z.string().nonempty(),
  internalName: z.string().nonempty(),
  availableFrom: dateSchema,
  availableTo: dateSchema.nullish(),
  website: z.string(),
  availableIn: z.array(z.string()),
  onboardingDuration: z.string().nullish(),
  shortDescription: z.string().nullish(),
  visibleToClients: z.boolean(),
  _productKind: z.string(),
  _termType: z.string(),
  _rateType: z.string(),
  _unitType: z.string(),
});

const identifiersSchema = z.object({
  isin: z.string(),
  apir: z.string(),
  cusip: z.string(),
  tickerSymbol: z.string(),
});

const { commonCountriesList, uncommonCountriesList } = getCountryLists();
const COUNTRY_OPTIONS: Option[] = [
  ...commonCountriesList,
  ...uncommonCountriesList,
];

type FormSchema = z.input<typeof formSchema>;

function AvailableInSelector({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  // Use a ref so the `value` prop passed to MultipleSelector never changes
  // after mount. MultipleSelector has a useEffect([value]) that resets its
  // internal `selected` state whenever value changes — if we let it update on
  // every form re-render it races against onSelect and reverts the append.
  const initialSelected = useRef(
    value.map(
      (code) =>
        COUNTRY_OPTIONS.find((o) => o.value === code) ?? {
          value: code,
          label: code,
        },
    ),
  );
  return (
    <MultipleSelector
      value={initialSelected.current}
      defaultOptions={COUNTRY_OPTIONS}
      onChange={(opts) => onChange(opts.map((o) => o.value))}
      placeholder="Select countries..."
      hidePlaceholderWhenSelected
    />
  );
}

export const Route = createFileRoute("/__auth/products_/$productId/")({
  head: () => opsHead("Product"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { productId } = Route.useParams();
  const { data, error } = useSuspenseQuery(
    graphql(`
      query ProductIndexPage($id: ID!) {
        product(id: $id) {
          id
          name
          internalName
          availableFrom
          availableTo
          website
          rateScrapingBaseUrl
          rateScrapingFormat
          rateScrapingEnabled
          defaultAssetCountry
          availableIn
          onboardingDuration
          shortDescription
          visibleToClients
          ... on BankAccountProduct {
            rate {
              id
              rate
              effectiveFromInclusive
              updatedAt
              tiers {
                id
                rate
                minBalanceInclusive
                maxBalanceExclusive
              }
            }
          }
          ... on FundProduct {
            cnav
            domicile
            isin
            apir
            cusip
            tickerSymbol
            latestRate {
              id
              effectiveToExclusive
              updatedAt
              value {
                ... on FundRateValue {
                  id
                  apy
                }
              }
            }
          }
          ... on TermDepositProduct {
            rateQuotes {
              id
              rate
              effectiveAt
              updatedAt
            }
          }
          ...EditRatesModal_Product
        }
      }
    `),
    { variables: { id: productId ?? "" } },
  );

  const product = data?.product;

  if (error) {
    throw error;
  }
  if (!product) {
    throw new NotFoundError();
  }

  const [editingRate, setEditingRate] = useState(false);
  const [editingRateScraping, setEditingRateScraping] = useState(false);

  let latestRate:
    | {
        kind: "fund" | "term_deposit" | "bank_account_basic";
        rate: Decimal;
        at: DateTime;
        updatedAt: DateTime;
      }
    | {
        kind: "bank_account_tiered";
        rates: BankAccountRateFlattened[];
        at: DateTime;
        updatedAt: DateTime;
      }
    | null;

  switch (product.__typename) {
    case "BankAccountProduct":
      latestRate = product.rate.tiers.length
        ? {
            kind: "bank_account_tiered",
            at: DateTime.fromJSDate(product.rate.effectiveFromInclusive),
            updatedAt: DateTime.fromJSDate(product.rate.updatedAt),
            rates: flattenBankAccountRate(product.rate),
          }
        : {
            kind: "bank_account_basic",
            at: DateTime.fromJSDate(product.rate.effectiveFromInclusive),
            updatedAt: DateTime.fromJSDate(product.rate.updatedAt),
            rate: product.rate.rate,
          };
      break;

    case "FundProduct": {
      const latestFundRate = product.latestRate;
      latestRate = latestFundRate
        ? {
            kind: "fund",
            rate: latestFundRate.value.apy,
            at: DateTime.fromJSDate(latestFundRate.effectiveToExclusive),
            updatedAt: DateTime.fromJSDate(latestFundRate.updatedAt),
          }
        : null;
      break;
    }

    case "TermDepositProduct": {
      const rateQuote = product.rateQuotes.at(0);
      if (!rateQuote) {
        throw new Error(`expected at least 1 rate quote`);
      }
      latestRate = {
        kind: "term_deposit",
        rate: rateQuote.rate,
        at: DateTime.fromJSDate(rateQuote.effectiveAt),
        updatedAt: DateTime.fromJSDate(rateQuote.updatedAt),
      };
      break;
    }

    default:
      neverFunc(product);
  }

  const [updateProduct] = useMutation(
    graphql(`
      mutation UpdateProduct($input: UpdateProductInput!) {
        updateProduct(input: $input) {
          product {
            id
            name
            internalName
            availableFrom
            availableTo
            website
            rateScrapingBaseUrl
            rateScrapingFormat
            rateScrapingEnabled
            availableIn
            onboardingDuration
            shortDescription
            visibleToClients
          }
        }
      }
    `),
  );

  const defaultValues: FormSchema = {
    ...product,
    website: product.website ?? "",
    availableIn: product.availableIn ?? ["AUS"],
    onboardingDuration: product.onboardingDuration ?? "",
    shortDescription: product.shortDescription ?? "",
    _productKind: productKindToFriendlyKind(product.__typename),
    _termType:
      product.__typename === "TermDepositProduct" ? "Fixed" : "Ongoing",
    _rateType: productKindToFriendlyRateKind(product.__typename),
    _unitType: product.__typename === "FundProduct" ? "Fund" : "Currency",
  };

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: formSchema,
    },
    async onSubmit(formData) {
      const parsedData = formSchema.parse(formData.value);
      await updateProduct({
        variables: {
          input: {
            productId: product.id,
            name: parsedData.name,
            internalName: parsedData.internalName,
            availableFrom: parsedData.availableFrom,
            availableTo: parsedData.availableTo,
            website: parsedData.website || null,
            availableIn: parsedData.availableIn,
            onboardingDuration: parsedData.onboardingDuration || null,
            shortDescription: parsedData.shortDescription || null,
            visibleToClients: parsedData.visibleToClients,
          },
        },
      })
        .then(() => {
          toast({ title: "Product updated", kind: "success", autoHide: true });
        })
        .catch(() => {
          toast({
            title: "Something went wrong",
            kind: "error",
            autoHide: false,
          });
        });
    },
  });

  const [updateFundProduct] = useMutation(
    graphql(`
      mutation UpdateFundProduct($input: UpdateFundProductInput!) {
        updateFundProduct(input: $input) {
          product {
            id
            isin
            apir
            cusip
            tickerSymbol
          }
        }
      }
    `),
  );

  const isFundProduct = product.__typename === "FundProduct";
  const identifiersForm = useAppForm({
    defaultValues: {
      isin: (isFundProduct ? product.isin : null) ?? "",
      apir: (isFundProduct ? product.apir : null) ?? "",
      cusip: (isFundProduct ? product.cusip : null) ?? "",
      tickerSymbol: (isFundProduct ? product.tickerSymbol : null) ?? "",
    },
    validators: {
      onSubmit: identifiersSchema,
    },
    async onSubmit(formData) {
      const parsedData = identifiersSchema.parse(formData.value);
      await updateFundProduct({
        variables: {
          input: {
            productId: product.id,
            isin: parsedData.isin || null,
            apir: parsedData.apir || null,
            cusip: parsedData.cusip || null,
            tickerSymbol: parsedData.tickerSymbol || null,
          },
        },
      })
        .then(() => {
          toast({ title: "Product updated", kind: "success", autoHide: true });
        })
        .catch(() => {
          toast({
            title: "Something went wrong",
            kind: "error",
            autoHide: false,
          });
        });
    },
  });

  return (
    <>
      <div className="flex flex-row items-stretch gap-6">
        <Card className="flex flex-grow gap-4 p-6">
          <div className="flex flex-1 flex-col">
            <form.AppForm>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  form.handleSubmit(e);
                }}
                className="flex h-full w-full flex-col gap-4"
              >
                <Txt variant="card-title">Details</Txt>
                <div className="grid grid-cols-2 gap-4">
                  <form.AppField name="name">
                    {(field) => <field.InputField label="Name" />}
                  </form.AppField>
                  <form.AppField name="internalName">
                    {(field) => <field.InputField label="Internal name" />}
                  </form.AppField>
                  <div className="col-span-2 flex flex-row items-end gap-2">
                    <div className="flex-1">
                      <form.AppField name="website">
                        {(field) => (
                          <field.InputField
                            label="Website (optional)"
                            placeholder="https://example.com"
                          />
                        )}
                      </form.AppField>
                    </div>
                    <form.Subscribe selector={(state) => state.values.website}>
                      {(website) =>
                        /^https?:\/\//.test(website) ? (
                          <a
                            href={website}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button
                              variant="outline"
                              icon={<ExternalLink />}
                              aria-label="Visit website"
                            />
                          </a>
                        ) : (
                          <Button
                            variant="outline"
                            icon={<ExternalLink />}
                            aria-label="Visit website"
                            disabled
                          />
                        )
                      }
                    </form.Subscribe>
                  </div>
                  <form.AppField name="_productKind">
                    {(field) => (
                      <field.InputField disabled label="Product kind" />
                    )}
                  </form.AppField>
                  <form.AppField name="_termType">
                    {(field) => <field.InputField disabled label="Term type" />}
                  </form.AppField>
                  <form.AppField name="_rateType">
                    {(field) => <field.InputField disabled label="Rate type" />}
                  </form.AppField>
                  <form.AppField name="_unitType">
                    {(field) => <field.InputField disabled label="Unit type" />}
                  </form.AppField>
                  <form.AppField name="availableFrom">
                    {(field) => (
                      <field.DatePickerField label="Available from" />
                    )}
                  </form.AppField>
                  <form.AppField name="availableTo">
                    {(field) => (
                      <field.DatePickerField
                        label="Available to"
                        hideClearButton={false}
                      />
                    )}
                  </form.AppField>
                  <div className="col-span-2">
                    <form.AppField name="availableIn">
                      {(field) => (
                        <field.AnnotatedField label="Available in">
                          <AvailableInSelector
                            value={field.state.value as string[]}
                            onChange={(v) => field.handleChange(v)}
                          />
                        </field.AnnotatedField>
                      )}
                    </form.AppField>
                  </div>
                  <div className="col-span-2">
                    <form.AppField name="visibleToClients">
                      {(field) => (
                        <field.SwitchField
                          label="Visible to clients"
                          description="Hidden products are not shown or offered in the customer portal. Ops can still use them, and the availability dates are unchanged."
                        />
                      )}
                    </form.AppField>
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="text-sm text-tertiary">
                      Default asset country
                    </p>
                    <p className="text-sm">
                      {product.defaultAssetCountry ?? "-"}
                    </p>
                  </div>
                  {product.__typename === "FundProduct" && (
                    <div className="flex flex-col gap-1">
                      <p className="text-sm text-tertiary">Domicile</p>
                      <p className="text-sm">{product.domicile ?? "-"}</p>
                    </div>
                  )}
                </div>
                {product.__typename === "FundProduct" && (
                  <div className="flex flex-col gap-1">
                    <p className="text-sm text-tertiary">CNAV</p>
                    <p className="text-sm">{product.cnav ? "Yes" : "No"}</p>
                  </div>
                )}
                <form.AppField name="onboardingDuration">
                  {(field) => (
                    <field.InputField
                      label="Onboarding duration (optional)"
                      placeholder="e.g. 10-14 days"
                    />
                  )}
                </form.AppField>
                <form.AppField name="shortDescription">
                  {(field) => (
                    <field.TextareaField
                      label="Short description (optional)"
                      rows={3}
                    />
                  )}
                </form.AppField>
                <div className="mt-auto flex flex-row justify-end">
                  <form.Subscribe selector={(f) => f.isDirty}>
                    {(isDirty) => <form.SubmitButton disabled={!isDirty} />}
                  </form.Subscribe>
                </div>
              </form>
            </form.AppForm>
          </div>
        </Card>

        <div className="flex w-[428px] flex-col gap-6">
          <Card className="flex flex-col gap-4 p-6">
            <div className="flex flex-row items-center justify-between">
              <Txt variant="card-title">Rates</Txt>
              <div className="flex flex-row gap-4">
                <Link to="rate-history">
                  <Button variant="outline">View rate history</Button>
                </Link>
                <Button
                  variant="primary"
                  onClick={() => {
                    setEditingRate(true);
                  }}
                >
                  Update rates
                </Button>
              </div>
            </div>
            {latestRate && (
              <>
                <div className="flex flex-col gap-4 rounded-lg bg-secondary p-4">
                  {(latestRate.kind === "fund" ||
                    latestRate.kind === "term_deposit" ||
                    latestRate.kind === "bank_account_basic") && (
                    <div className="flex flex-col gap-2">
                      <p className="text-secondary text-sm">
                        {product.__typename === "BankAccountProduct"
                          ? "Current rate"
                          : "Rate"}
                      </p>
                      <p className="text-black text-xl">
                        {formatRate(latestRate.rate)}
                      </p>
                    </div>
                  )}
                  {latestRate.kind === "bank_account_tiered" && (
                    <div className="flex flex-row gap-6">
                      {latestRate.rates.map((rate) => (
                        <div key={rate.id} className="flex flex-col gap-2">
                          <p className="text-secondary text-sm">
                            $
                            {rate.minBalanceInclusive.isZero()
                              ? 0
                              : rate.minBalanceInclusive
                                  .div(1_000_000)
                                  .toDP(2) + "m"}
                            {rate.maxBalanceExclusive
                              ? ` - $${rate.maxBalanceExclusive
                                  .div(1_000_000)
                                  .toDP(2)}m`
                              : "+"}
                          </p>
                          <p className="text-black text-xl">
                            {formatRate(rate.rate)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-col gap-2">
                    <p className="text-secondary text-sm">
                      {product.__typename === "BankAccountProduct"
                        ? "Effective from"
                        : "As of"}
                    </p>
                    <p className="text-black text-xl">
                      {latestRate.at.toLocaleString(DateTime.DATE_MED)}
                    </p>
                  </div>
                </div>
                <div className="flex flex-row items-center justify-between">
                  <p className="text-secondary text-xs">
                    Last updated{" "}
                    {latestRate.updatedAt.toLocaleString(DateTime.DATETIME_MED)}
                  </p>
                  <div className="flex flex-row items-center gap-1">
                    <RateSourceBadge
                      rateScrapingEnabled={product.rateScrapingEnabled}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      icon={<PiPencilSimple />}
                      aria-label="Edit rate scraping"
                      onClick={() => setEditingRateScraping(true)}
                    />
                  </div>
                </div>
              </>
            )}
          </Card>

          {product.__typename === "FundProduct" && (
            <Card className="flex flex-col gap-4 p-6">
              <identifiersForm.AppForm>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    identifiersForm.handleSubmit(e);
                  }}
                  className="flex w-full flex-col gap-4"
                >
                  <Txt variant="card-title">Identifiers</Txt>
                  <identifiersForm.AppField name="isin">
                    {(field) => (
                      <field.InputField
                        label="ISIN (optional)"
                        placeholder="e.g. IE00B44BQ083"
                      />
                    )}
                  </identifiersForm.AppField>
                  <identifiersForm.AppField name="apir">
                    {(field) => (
                      <field.InputField
                        label="APIR code (optional)"
                        placeholder="e.g. ETL0018AU"
                      />
                    )}
                  </identifiersForm.AppField>
                  <identifiersForm.AppField name="cusip">
                    {(field) => (
                      <field.InputField
                        label="CUSIP (optional)"
                        placeholder="e.g. 037833100"
                      />
                    )}
                  </identifiersForm.AppField>
                  <identifiersForm.AppField name="tickerSymbol">
                    {(field) => (
                      <field.InputField
                        label="Ticker symbol (optional)"
                        placeholder="e.g. AAA"
                      />
                    )}
                  </identifiersForm.AppField>
                  <div className="flex flex-row justify-end">
                    <identifiersForm.Subscribe selector={(f) => f.isDirty}>
                      {(isDirty) => (
                        <identifiersForm.SubmitButton disabled={!isDirty} />
                      )}
                    </identifiersForm.Subscribe>
                  </div>
                </form>
              </identifiersForm.AppForm>
            </Card>
          )}
        </div>
      </div>

      <EditRatesModal
        open={editingRate}
        onOpenChange={setEditingRate}
        product={product}
      />

      <RateScrapingModal
        open={editingRateScraping}
        onOpenChange={setEditingRateScraping}
        product={{
          id: product.id,
          availableIn: product.availableIn ?? ["AUS"],
          rateScrapingBaseUrl: product.rateScrapingBaseUrl ?? null,
          rateScrapingFormat: product.rateScrapingFormat ?? null,
          rateScrapingEnabled: product.rateScrapingEnabled,
        }}
      />
    </>
  );
}

function productKindToFriendlyKind(
  kind: PossibleTypesResultData["possibleTypes"]["Product"][number],
): string {
  switch (kind) {
    case "BankAccountProduct":
      return "Bank account";
    case "FundProduct":
      return "Fund";
    case "TermDepositProduct":
      return "Term deposit";
  }
}

function productKindToFriendlyRateKind(
  kind: PossibleTypesResultData["possibleTypes"]["Product"][number],
): string {
  switch (kind) {
    case "BankAccountProduct":
      return "Variable";
    case "FundProduct":
      return "Floating";
    case "TermDepositProduct":
      return "Fixed";
  }
}
